/**
 * SegmentedProgressBar
 *
 * Slide-granular timeline for the public video player. Instead of a continuous
 * scrubber (which allowed seeking to arbitrary mid-slide times and produced
 * visible audio/animation drift), the bar is divided into one segment per slide
 * — the Instagram-Stories / YouTube-chapters pattern.
 *
 * Behavior:
 * - Segment WIDTH is proportional to slide duration, so the bar still reads like
 *   a video timeline.
 * - Past slides render full, future slides empty, and the ACTIVE slide fills live
 *   from the controller's own `currentSlideTime / currentSlideDuration` — so the
 *   fill never disagrees with what's playing (no drift math here).
 * - Clicking / tapping a segment calls `onSelect(index)`. The parent seeks to that
 *   slide's start, so a slide ALWAYS begins from the beginning. There is no
 *   drag-to-scrub within a slide.
 */

import React, { useRef, useState } from 'react';
import { cn } from '../../lib/utils';

export interface ProgressSegment {
  /** Slide id (for keys / debugging) */
  id: string;
  /** Optional slide title shown in the hover tooltip */
  title?: string;
  /** Slide content duration in seconds — drives segment width */
  duration: number;
}

export interface SegmentedProgressBarProps {
  segments: ProgressSegment[];
  /** Index of the slide currently playing */
  currentSlideIndex: number;
  /** Elapsed time within the active slide (seconds) */
  currentSlideTime: number;
  /** Total duration of the active slide (seconds) */
  currentSlideDuration: number;
  /** Called with the target slide index when a segment is clicked/tapped */
  onSelect: (index: number) => void;
  /** Disables interaction and dims the bar */
  disabled?: boolean;
  /** Tighter sizing for narrow containers */
  compact?: boolean;
  /**
   * Resting-state presentation: ultra-thin, pinned-to-edge, NON-interactive (no tap targets,
   * no hover, no tooltips). Used as the always-visible progress indicator that doesn't cover
   * content; the full controls (with the interactive bar) are summoned by tapping the video.
   */
  slim?: boolean;
  className?: string;
}

export const SegmentedProgressBar: React.FC<SegmentedProgressBarProps> = ({
  segments,
  currentSlideIndex,
  currentSlideTime,
  currentSlideDuration,
  onSelect,
  disabled = false,
  compact = false,
  slim = false,
  className,
}) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  // In slim resting mode the bar is purely informational — no taps, hover, or tooltips.
  const interactive = !slim && !disabled;

  // Fraction filled for a given segment (0..1).
  const fillFor = (index: number): number => {
    if (index < currentSlideIndex) return 1;
    if (index > currentSlideIndex) return 0;
    // Active slide — derive from the controller's exact slide-relative time.
    if (!currentSlideDuration || currentSlideDuration <= 0) return 0;
    return Math.max(0, Math.min(1, currentSlideTime / currentSlideDuration));
  };

  return (
    <div
      ref={containerRef}
      className={cn(
        'relative flex items-center w-full select-none',
        slim ? 'h-1.5 gap-[2px]' : compact ? 'h-6 gap-[3px]' : 'h-8 gap-1',
        slim ? 'pointer-events-none' : disabled ? 'opacity-60' : 'cursor-pointer',
        className
      )}
    >
      {segments.map((seg, index) => {
        const fill = fillFor(index);
        const isActive = index === currentSlideIndex;
        return (
          <div
            key={seg.id}
            role={interactive ? 'button' : undefined}
            tabIndex={interactive ? 0 : -1}
            aria-label={interactive ? `Go to slide ${index + 1}${seg.title ? `: ${seg.title}` : ''}` : undefined}
            // flexGrow ∝ duration so wider slides occupy more of the bar.
            style={{ flexGrow: Math.max(seg.duration, 0.001), flexBasis: 0 }}
            className="group/seg relative h-full flex items-center min-w-[4px]"
            onMouseEnter={interactive ? () => setHoverIndex(index) : undefined}
            onMouseLeave={interactive ? () => setHoverIndex((cur) => (cur === index ? null : cur)) : undefined}
            onClick={interactive ? () => onSelect(index) : undefined}
            onKeyDown={interactive ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(index);
              }
            } : undefined}
          >
            {/* Track */}
            <div
              className={cn(
                'relative w-full rounded-full bg-white/25 overflow-hidden transition-all duration-200',
                slim ? 'h-[3px]' : compact ? 'h-[3px]' : 'h-1.5',
                interactive && 'group-hover/seg:h-2'
              )}
            >
              {/* Fill */}
              <div
                className={cn(
                  'absolute left-0 top-0 h-full rounded-full bg-primary',
                  // Active slide fills smoothly with playback; jumps are instant.
                  isActive ? 'transition-none' : 'transition-[width] duration-200'
                )}
                style={{ width: `${fill * 100}%` }}
              />
            </div>

            {/* Hover tooltip — slide number + title (interactive only) */}
            {interactive && hoverIndex === index && (
              <div className="absolute -top-9 left-1/2 -translate-x-1/2 whitespace-nowrap bg-black/80 text-white text-xs px-2 py-1 rounded-md border border-white/10 shadow-xl pointer-events-none z-50 backdrop-blur-sm">
                {seg.title ? `${index + 1}. ${seg.title}` : `Slide ${index + 1}`}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
